// Extract direction from leftLabel (e.g., "Gest. Len - Longer" -> "Longer")
export function getLeftDirection(leftLabel: string): string {
  const parts = leftLabel.split(' - ');
  return parts[1] || '';
}

/**
 * Parse accuracy string to proportion (0–1). Handles "45", "45%", "".
 * Used for i4-style uncertainty band: width = 2(1 - r²) in SD units.
 */
export function parseAccuracyToProportion(accuracy: string): number {
  const sanitized = (accuracy || '').replace(/%/g, '').trim();
  if (sanitized === '') return 0;
  const parsed = parseFloat(sanitized);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(1, parsed / 100));
}

/**
 * Uncertainty band half-width as % of bar (i4 formula).
 * Bar spans ±4 SD (8 SD total) so 1 SD = 12.5%. Half-width in SD = (1 - r²).
 * Returns (1 - r²) * 12.5 so full width = 25 * (1 - r²) %.
 */
export function getUncertaintyHalfWidthPercent(accuracy: string): number {
  const proportion = parseAccuracyToProportion(accuracy);
  const oneMinusR2 = 1 - proportion * proportion;
  return oneMinusR2 * 12.5;
}

export const ACCURACY_DOT_TIERS = [
  { label: 'Greater than 80%', color: '#4ade80' },
  { label: '50 - 79%', color: '#fbbf24' },
  { label: 'Less than 50%', color: '#f87171' },
] as const;

export function getAccuracyDotColor(accuracy: string): string {
  const proportion = parseAccuracyToProportion(accuracy) * 100;
  if (proportion >= 80) return '#4ade80';
  if (proportion >= 50) return '#fbbf24';
  return '#f87171';
}

export function getGuidePosition(percentile: number): string {
  const axisPercentile = percentile === 1 ? 0 : percentile;
  // Axis is rendered 100 (left) to 0 (right).
  return `${100 - axisPercentile}%`;
}

// Calculate bar position based on Standard Deviation (like i4)
export function calculateBarPosition(
  value: string,
  breedAvg: string,
  lowValue: string,
  topValue: string,
  standardError: string,
): number | null {
  const ebvValue = parseFloat(value);
  const avgValue = parseFloat(breedAvg);
  const seValue = parseFloat(standardError);
  const q100 = parseFloat(lowValue);
  const q000 = parseFloat(topValue);

  if (isNaN(ebvValue)) return null;

  const isFlipped = !isNaN(q100) && !isNaN(q000) && q100 > q000;

  if (!isNaN(avgValue) && !isNaN(seValue) && seValue > 0) {
    const stdDev = Math.sqrt(seValue);
    if (stdDev === 0) return null;

    let deviation = (ebvValue - avgValue) / stdDev;

    if (isFlipped) {
      deviation = -deviation;
    }

    const clampedDeviation = Math.max(-4, Math.min(4, deviation));

    return 50 + clampedDeviation * 12.5;
  }

  return null;
}

// Get range values - CALCULATED from breedAvg ± 4*sqrt(SE) like i4
export function getRangeValues(
  lowValue: string,
  topValue: string,
  breedAvg: string,
  standardError: string,
  decimals: number,
): { leftValue: string; rightValue: string; isFlipped: boolean } {
  const avg = parseFloat(breedAvg);
  const standardErrorValue = parseFloat(standardError);
  const q100 = parseFloat(lowValue);
  const q000 = parseFloat(topValue);

  const isFlipped = !isNaN(q100) && !isNaN(q000) && q100 > q000;

  let calculatedLow = 0;
  let calculatedTop = 0;

  if (!isNaN(avg) && !isNaN(standardErrorValue) && standardErrorValue > 0) {
    const stdDev = Math.sqrt(standardErrorValue);
    calculatedLow = avg - 4 * stdDev;
    calculatedTop = avg + 4 * stdDev;
    calculatedLow = Math.round(calculatedLow * 100) / 100;
    calculatedTop = Math.round(calculatedTop * 100) / 100;
  }

  const formatValue = (rangeValue: number) => {
    if (isNaN(rangeValue) || rangeValue === 0) return '';
    const formatted = rangeValue.toFixed(decimals);
    return rangeValue >= 0 ? `+${formatted}` : formatted;
  };

  if (isFlipped) {
    return {
      leftValue: formatValue(calculatedTop),
      rightValue: formatValue(calculatedLow),
      isFlipped,
    };
  }

  return {
    leftValue: formatValue(calculatedLow),
    rightValue: formatValue(calculatedTop),
    isFlipped,
  };
}
